import {numberFormat, manWonFormat} from '@shared/helpers/format'
import {eq} from '@shared/helpers/lang'
import {roundUpToTenThousand} from '@shared/helpers/math'

import {MaximumUsingChargePointAmount, PayMoneyUserStatus, PayPointAmount} from '@web/core/constants'

/** 충전 가능 금액 */
export function getRechargeablePoint({
    rechargePoint,
    maximumRechargingAmount,
    minimumRechargingAmount = PayPointAmount.MINIMUM_RECHARGE_AMOUNT,
}: {
    /** 충전할 금액 */
    rechargePoint: number
    /** 잔여 충전 가능 잔액 */
    maximumRechargingAmount: number
    /** 최소 충전 금액 */
    minimumRechargingAmount?: number
}): number {
    if (maximumRechargingAmount < rechargePoint) {
        /** 잔여 충전 가능 잔액 보다 입력값이 크다면, 최대 잔여 충전 가능금액 반영 */
        return maximumRechargingAmount
    } else if (rechargePoint <= minimumRechargingAmount) {
        /** 1만원 이하라면 10000 원 적용 */
        /** 페이머니 유저는 0원 적용 가능 */
        return minimumRechargingAmount
    } else if (rechargePoint % 10000) {
        /** 1만원 단위가 아닌 경우 만원 단위로 반영 */
        const addingAmount = roundUpToTenThousand(rechargePoint, 10000)
        if (addingAmount > maximumRechargingAmount) {
            return maximumRechargingAmount
        } else {
            return addingAmount
        }
    }
    return rechargePoint
}

/**
 * 충전결제 (결제+입력) 가능 여부
 * - 상품금액 + 배송비 - 할인금액 - 네이버 포인트 사용후 남은 금액이 있거나,
 * - 후불결제가 사용되지 않았다면
 */
export function isRechargePointEnabled({
    reloadableRechargePointAmount,
    remainOrderAmount,
    payLaterUsed,
    canUseRechargeAccount,
    canUseChargePointAccount = false,
    payMoneyStateAvailable = false,
}: {
    /** 잔여 충전 가능 잔액 */
    reloadableRechargePointAmount: number
    /** 남은 금액 */
    remainOrderAmount: number
    /** 후불결제 사용여부 */
    payLaterUsed: boolean
    /** 충전계좌 결제 가능 여부 */
    canUseRechargeAccount: boolean
    /** 페이머니 계좌 결제 가능 여부 */
    canUseChargePointAccount?: boolean
    /** 페이머니 유저 여부 */
    payMoneyStateAvailable?: boolean
}): boolean {
    if (payMoneyStateAvailable) {
        return remainOrderAmount > 0 && canUseRechargeAccount && canUseChargePointAccount
    }
    return (
        remainOrderAmount > 0 &&
        reloadableRechargePointAmount >= PayPointAmount.MINIMUM_RECHARGE_AMOUNT &&
        !payLaterUsed &&
        canUseRechargeAccount
    )
}

/** 충전 포인트 입력을 검증합니다 */
export function validateRechargingPoint({
    isPayMoneyPolicy = false,
    rechargePoint = 0,
    chargePointAmount = 0,
    maximumRechargingAmount = 0,
    incorrectUnit = 0,
}: {
    /** 입력된 충전 포인트 */
    rechargePoint?: number
    /** 페이머니 정책 적용 여부 */
    isPayMoneyPolicy?: boolean
    /** 충전 포인트 잔액 */
    chargePointAmount?: number
    /** 최대 충전 가능 금액 */
    maximumRechargingAmount?: number
    /** 단위 체크 (0 이면 단위체크 X) */
    incorrectUnit?: number
}): {isValid: boolean; error?: {isOver: boolean; isUnder: boolean; isIncorrectUnit: boolean; message: string}} {
    const error = (function () {
        /** 1만원 미만 < 입력 */
        const isBaseUnder = rechargePoint < PayPointAmount.MINIMUM_RECHARGE_AMOUNT
        /** 200만 초과 < 입력 */
        const isBaseOver = rechargePoint > maximumRechargingAmount
        /** 1만원 단위가 아니라면 */
        const isIncorrectUnit = rechargePoint % incorrectUnit > 0 && rechargePoint !== maximumRechargingAmount
        if (isPayMoneyPolicy) {
            if (chargePointAmount <= 0) {
                return {
                    isUnder: isBaseUnder,
                    isOver: isBaseOver,
                    isIncorrectUnit,
                    message: `최소 1만원부터 1만원 단위로 최대 ${manWonFormat(
                        maximumRechargingAmount,
                    )}까지 충전 가능합니다.`,
                }
            }
            return {
                /** 0원 입력 가능 */
                /** 1원 이상 1만원 미만 입력시 에러 */
                isUnder: isBaseUnder && rechargePoint > 0,
                isOver: isBaseOver,
                isIncorrectUnit,
                message: `1만원 단위로 최대 ${manWonFormat(maximumRechargingAmount)}까지 충전 가능합니다.`,
            }
        }
        return {
            isUnder: isBaseUnder,
            isOver: isBaseOver,
            isIncorrectUnit,
            message: `최소 1만원부터 1만원 단위로 최대 ${numberFormat(maximumRechargingAmount)}원까지 충전 가능합니다.`,
        }
    })()

    const isValid = !(error.isUnder || error.isOver || error.isIncorrectUnit)

    return {
        isValid,
        error: !isValid ? error : undefined,
    }
}

/**
 * 페이머니 계좌 결제 가능 여부 */
export function isPointChargingAvailable({
    payMoneyUserStatus,
}: {
    payMoneyUserStatus?: Maybe<PayMoneyUserStatusType>
}): boolean {
    return eq(PayMoneyUserStatus.OPEN, payMoneyUserStatus)
}

/** 포인트 충전 결제 시도시 상태 정책 */
export function getPointRechargingPaymentPreparationInfo({
    pointRechargingIsUsed,
    payMoneyStateAvailable,
    payMoneyInfoIsLoaded,
    pointChargingLimitIsLoaded,
    payMoneyBankAccountIsConnected,
    usingChargePointAmount,
    payAmount,
}: {
    /** 충전포인트 충전 사용여부 */
    pointRechargingIsUsed: boolean
    /** 페이머니 유저 여부 */
    payMoneyStateAvailable: boolean
    /** 페이머니 정보 로드 여부 */
    payMoneyInfoIsLoaded: boolean
    /** 충전 포인트 한도 로드 여부 */
    pointChargingLimitIsLoaded: boolean
    /** 페이머니 통장 연결 여부 */
    payMoneyBankAccountIsConnected: boolean
    /** 사용 예정 충전포인트 */
    usingChargePointAmount: number
    /** 결제수단으로 넘어온 결제금액 중 충전결제 제외한 수단으로 결제할 금액 */
    payAmount: number
}) {
    const anyEssentialInfoIsLoaded = pointChargingLimitIsLoaded || payMoneyBankAccountIsConnected
    /** 충전결제 외 결제수단으로 결제할 금액 > 0 여부 */
    const isPayAmountExists = !!payAmount
    /** 페이머니 회원조회 성공 && ((충전한도 조회 성공 or 4.0 유저의 경우) or 결제수단으로 넘어온 금액 0 일때 결제 가능) */
    const pointRechargingAvailable = payMoneyInfoIsLoaded && (anyEssentialInfoIsLoaded || !isPayAmountExists)
    /** 사용금액이 결제가능페이머니 한도내에 있는지 여부 (초과했으면 false) */
    const isRechargingPointAmountUnderMaximumAmount = usingChargePointAmount < MaximumUsingChargePointAmount
    /*
     * 페이머니 회원 Case
     * >> 페이머니 사용한도를 초과하지 않아야 추가충전가능
     * 페이머니 회원 X Case
     * >> 충전결제 사용가능해야 (점검상태가 아니거나 등등) 추가충전가능
     */
    const pointRechargingRequired = payMoneyStateAvailable
        ? isPayAmountExists && isRechargingPointAmountUnderMaximumAmount
        : pointRechargingIsUsed
    /** 페이머니 회원인 경우에만 보유중인 페이머니가 있다면 은행 점검 상태라도 결제 가능 */
    const payableRegardlessOfBankMaintenance = payMoneyStateAvailable ? usingChargePointAmount !== 0 : false
    /** 페이머니 회원이 아니거나 남은 페이머니가 없고, 충전한도 조회 실패 상태면 은행점검중에는 결제 불가 */
    const unavailableDueToBankMaintenance = !payableRegardlessOfBankMaintenance && !pointChargingLimitIsLoaded

    return {
        /** 결제수단 > 충전결제로 결제가능여부 (충전이 불가해도 결제가능한 경우 존재) */
        pointRechargingAvailable,
        /** 추가충전 가능여부 */
        pointRechargingRequired,
        /** 은행 점검상태에서 결제 가능 여부 */
        payableRegardlessOfBankMaintenance,
        /** 은행 점검으로 인해 결제 불가 */
        unavailableDueToBankMaintenance,
    }
}

/** 은행 계좌에서 충전 시도할 떄 계좌 관련 정책 */
export function getBankAccountPolicyForPointCharging({
    registeredBankAccountList,
    availableAccountKey,
}: {
    registeredBankAccountList: {payable: boolean; accountKey: string}[]
    availableAccountKey: Maybe<string>
}) {
    const noRegisteredBankAccount = !registeredBankAccountList.length
    const payableBankAccountExists =
        registeredBankAccountList.find(({accountKey}) => accountKey === availableAccountKey)?.payable ?? false

    return {
        /** 계좌가 등록되지 않은경우 */
        noRegisteredBankAccount,
        /** 선택계좌 상태가 지급가능이 아닌 경우 */
        payableBankAccountExists,
    }
}
